// Clasificación uniforme de asuntos/documentos en apartados (FASE 9).
// Vivienda: Contratos / Recibos / Seguros / Varios
// Persona (Contratos y seguros): Seguros / Contratos / Suscripciones / Varios

const norm = (v) =>
  (v == null ? "" : String(v))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const val = (obj, k) => (obj && obj[k] && obj[k].valor != null ? obj[k].valor : null);

export const getMatterDoc = (matter, documents) =>
  (documents || []).find((d) => (matter?.documents || []).includes(d.id)) || null;

// Señales combinadas de un asunto + su documento principal.
const signals = (matter, doc) => {
  const an = (doc && doc.analisis) || {};
  const viv = an.vivienda || {};
  const td = norm(val(an, "tipo_documento") || matter?.matter_type);
  const ts = norm(val(viv, "tipo_suministro"));
  const aseg = val(viv, "aseguradora");
  const per = norm(matter?.periodicity || val(an, "periodicidad"));
  const text = norm(
    [td, matter?.title, matter?.matter_type, matter?.provider, val(an, "informacion_relevante")]
      .filter(Boolean)
      .join(" ")
  );
  return { td, ts, aseg, per, text };
};

// ---- Vivienda ----
export const VIVIENDA_SECCIONES = [
  { id: "contratos", label: "Contratos", icon: "FileSignature" },
  { id: "recibos", label: "Recibos", icon: "Receipt" },
  { id: "seguros", label: "Seguros", icon: "ShieldCheck" },
  { id: "varios", label: "Varios", icon: "FolderOpen" },
];

const UTIL_SERVICIOS = ["electricidad", "gas", "agua", "internet", "telefonia", "alarma", "mantenimiento"];

export const seccionVivienda = (matter, doc) => {
  const { ts, aseg, per, text } = signals(matter, doc);
  // 1. Seguros
  if (ts === "seguro_hogar" || aseg || /\bseguro|p[o]liza|aseguradora\b/.test(text)) return "seguros";
  // 2. Contrato explícito (y no es un recibo/factura)
  if (/contrato/.test(text) && !/\b(recibo|factura)\b/.test(text)) return "contratos";
  // 3. Recibo/factura explícito
  if (/\b(recibo|factura)\b/.test(text)) return "recibos";
  // 4. IBI / comunidad
  if (ts === "ibi" || ts === "comunidad" || /\bibi\b|comunidad de propietarios|cuota de comunidad/.test(text))
    return "recibos";
  // 5. Documentos "Varios" por naturaleza
  if (/comunicaci[o]n|certificad|garant[i]a|licencia|inspecci[o]n|\binforme\b|boletin/.test(text)) return "varios";
  // 6. Tasas / basuras periódicas
  if (/\btasa|basura|residuos\b/.test(text)) return "recibos";
  // 7. Servicio de la vivienda sin más contexto → Contratos
  if (UTIL_SERVICIOS.includes(ts)) return "contratos";
  // 8. Periódico genérico → Recibos
  if (/mensual|bimestral|trimestral|anual/.test(per)) return "recibos";
  return "varios";
};

// ---- Vehículo ----
export const VEHICULO_SECCIONES = [
  { id: "documentacion", label: "Documentación", icon: "FileText" },
  { id: "mantenimiento", label: "Mantenimiento", icon: "Wrench" },
  { id: "seguros", label: "Seguros", icon: "ShieldCheck" },
  { id: "varios", label: "Varios", icon: "FolderOpen" },
];

export const seccionVehiculo = (matter, doc) => {
  const an = (doc && doc.analisis) || {};
  const veh = an.vehiculo || {};
  const tdv = norm(val(veh, "tipo_doc_vehiculo"));
  const { td, text } = signals(matter, doc);
  const t = `${tdv} ${td} ${text}`;
  // 1. Seguros
  if (tdv === "seguro" || /\bseguro|p[o]liza|asistencia en carretera\b/.test(t)) return "seguros";
  // 2. Mantenimiento
  if (tdv === "mantenimiento" ||
      /taller|revisi[o]n|reparaci[o]n|neum[a]tic|aceite|freno|mantenimiento|itv aprobada/.test(t)) return "mantenimiento";
  // 3. Documentación oficial
  if (["permiso_circulacion", "ficha_tecnica", "itv", "impuesto"].includes(tdv) ||
      /permiso de circulaci[o]n|ficha t[e]cnica|\bitv\b|impuesto|tarjeta de inspecci[o]n|matriculaci[o]n/.test(t)) return "documentacion";
  // 4. Varios
  if (/garant[i]a|campa[n]a|comunicaci[o]n|certificad/.test(t)) return "varios";
  return "varios";
};

// ---- Persona (Contratos y servicios personales; clave interna "Contratos y seguros") ----
export const PERSONA_SECCIONES = [
  { id: "seguros", label: "Seguros", icon: "ShieldCheck" },
  { id: "contratos", label: "Contratos", icon: "FileSignature" },
  { id: "suscripciones", label: "Suscripciones y cuotas", icon: "RefreshCw" },
  { id: "varios", label: "Varios", icon: "FolderOpen" },
];

export const seccionPersona = (matter, doc) => {
  const { per, text } = signals(matter, doc);
  // 1. Seguros personales: vida, salud, decesos, accidentes, RC personal…
  if (/\bseguro|p[o]liza|aseguradora|decesos|cobertura|responsabilidad civil|mutua\b/.test(text)) return "seguros";
  // 2. Suscripciones y cuotas: asociaciones, clubes, gimnasios, colegios profesionales, academias, plataformas…
  if (/suscripci[o]n|cuota|asociaci[o]n|club|gimnasio|colegio (oficial|profesional)|colegiad|socio|academia|plataforma|streaming|netflix|spotify|prime|disney|hbo|movistar\+|dazn|membres[i]a|abono/.test(text))
    return "suscripciones";
  // 3. Varios: comunicaciones y certificados personales
  if (/comunicaci[o]n|certificad|notificaci[o]n|carta/.test(text)) return "varios";
  // 4. Contratos personales / servicios profesionales / asesorías
  if (/contrato|asesor|servicio|abogad|gestor[i]?a|consultor|notar/.test(text)) return "contratos";
  // 5. Periódico sin más contexto → cuota
  if (/mensual|trimestral|anual/.test(per)) return "suscripciones";
  return "varios";
};

// ---- Mascotas ----
export const MASCOTA_SECCIONES = [
  { id: "pasaporte", label: "Pasaporte", icon: "BookUser" },
  { id: "veterinario", label: "Veterinario", icon: "Stethoscope" },
  { id: "seguros", label: "Seguros", icon: "ShieldCheck" },
  { id: "varios", label: "Varios", icon: "FolderOpen" },
];

export const seccionMascota = (matter, doc) => {
  const { text } = signals(matter, doc);
  if (/\bseguro|p[o]liza|aseguradora/.test(text)) return "seguros";
  if (/pasaporte|microchip|\bchip\b|cartilla|identificaci[o]n|registro|censo|licencia/.test(text)) return "pasaporte";
  if (/veterinari|vacuna|desparasit|cl[i]nica|consulta|analit|revisi[o]n|tratamiento|cirug/.test(text)) return "veterinario";
  return "varios";
};
